import { lazy, Suspense, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';
import './features/trust.css';
import BrowsePage from './components/BrowsePage';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import PropertyDetailsPage from './components/PropertyDetailsPage';
import SellerProfilePage from './components/SellerProfilePage';
import SignUpPage from './components/SignUpPage';
import AdvertisePage from './components/AdvertisePage';
import SiteHeader from './components/SiteHeader';
import MobileBottomNav from './components/MobileBottomNav';
import MobileMenuSheet from './components/MobileMenuSheet';
import SiteJsonLd from './components/SiteJsonLd';
import GoogleOneTapPrompt from './components/GoogleOneTapPrompt';
import TwoFactorPromptModal from './components/TwoFactorPromptModal';
// import VerifyContactModal from './components/VerifyContactModal';
import SiteFooter from './components/SiteFooter';
import CookieConsentBanner from './components/CookieConsentBanner';
import HelpBotWidget from './components/HelpBotWidget';
import CookieSettingsModal from './components/CookieSettingsModal';
import AboutPage from './components/AboutPage';
import PrivacyPage from './components/PrivacyPage';
import CookiePolicyPage from './components/CookiePolicyPage';
import TermsPage from './components/TermsPage';
import ContactPage from './components/ContactPage';
import SafetyPage from './components/SafetyPage';
import ListingPolicyPage from './components/ListingPolicyPage';
import AgentVerificationPolicyPage from './components/AgentVerificationPolicyPage';
import CareersPage from './components/CareersPage';
import LocationLandingPage from './components/LocationLandingPage';
import SellerGuidePage from './components/SellerGuidePage';
import { BlogIndexPage, BlogArticlePage } from './components/BlogPage';
import { HelpCentreIndexPage, HelpArticlePage } from './components/HelpCentrePage';
import BlogGuidelinesPage from './components/BlogGuidelinesPage';
import { Alert, LoadingSpinner } from './components';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ADMIN_FEATURE_FLAGS } from './config/adminFeatureFlags';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { CookieConsentProvider, useCookieConsent } from './context/CookieConsentContext';
import { usePropertyFilters } from './hooks/usePropertyFilters';
import { usePropertyForm } from './hooks/usePropertyForm';
import { useProperties } from './hooks/useProperties';
import { placeholderImage } from './propertyConfig';
import { API_BASE } from './config';

import { generatePropertySlug } from './seoUtils';
import { logPageView } from './firebaseAnalytics';
import { trackEvent } from './analyticsLoader';

// Code-split the auth-gated dashboards, settings, and demo pages away from
// the public marketing/listing routes above - these are never hit by
// anonymous organic search traffic, so there's no SEO/first-paint cost to
// deferring them, and it keeps the main bundle from shipping the full Admin
// dashboard (and its own dependencies) to someone who just wants to view a
// property listing. Public/high-traffic/SEO routes (Landing, Browse,
// PropertyDetails, LocationLanding, Blog, SellerGuide, SellerProfile,
// Login/SignUp, Advertise) stay eager on purpose - no loading flash on the
// pages that most need to render immediately.
const AdminDashboardPage = lazy(() => import('./components/AdminDashboardPage'));
const AgentDashboardPage = lazy(() => import('./components/AgentDashboardPage'));
const DashboardPage = lazy(() => import('./components/DashboardPage'));
const MessagesInboxPage = lazy(() => import('./components/MessagesInboxPage'));
const UserSettingsPage = lazy(() => import('./components/UserSettingsPage'));
const FavoritesPage = lazy(() => import('./components/FavoritesPage'));
const ListPropertyPage = lazy(() => import('./components/ListPropertyPage'));
const EditPropertyPage = lazy(() => import('./components/EditPropertyPage'));
const ForgotPasswordPage = lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const ConfirmEmailPage = lazy(() => import('./components/ConfirmEmailPage'));
const CheckYourEmailPage = lazy(() => import('./components/CheckYourEmailPage'));
const AdvertisePaymentCallbackPage = lazy(() =>
  import('./components/AdvertisePaymentCallbackPage'),
);
const MyBookingsPage = lazy(() => import('./components/MyBookingsPage'));
const MyReservationRequestsPage = lazy(() =>
  import('./components/MyReservationRequestsPage'),
);
const BecomeAgentPage = lazy(() => import('./components/BecomeAgentPage'));
const ContributorDashboardPage = lazy(() => import('./components/ContributorDashboardPage'));
const ArticleEditorPage = lazy(() => import('./components/ArticleEditorPage'));
const TwoFactorSetupPage = lazy(() => import('./components/TwoFactorSetupPage'));
const SellerProfileTemplateDemo = lazy(() => import('./components/SellerProfileTemplateDemo'));

function RouteLoadingFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
      <LoadingSpinner size="lg" label="Loading…" />
    </div>
  );
}

function LazyRoute({ children }) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

function RedirectToSellerProfile() {
  const { userId } = useParams();
  return <Navigate to={`/seller/${userId}`} replace />;
}

// Google's identity SDK (loaded by GoogleOAuthProvider as soon as it mounts,
// regardless of whether a child actually uses it yet) must not load for
// every visitor on every page - only where Google auth is actually offered.
// Callers mount GoogleOAuthProvider conditionally (login/signup routes,
// and the global One Tap prompt once functional-cookie consent is given)
// instead of wrapping the whole app unconditionally at the root.
function googleClientId() {
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const productionHostnames = String(process.env.REACT_APP_PRODUCTION_HOSTNAMES || '')
    .split(',')
    .map((hostname) => hostname.trim().toLowerCase())
    .filter(Boolean);
  const currentHostname = String(window.location.hostname || '').toLowerCase();
  const isConfiguredProductionHost = productionHostnames.includes(currentHostname);

  const clientId =
    !isLocalHost && isConfiguredProductionHost
      ? process.env.REACT_APP_GOOGLE_CLIENT_ID_PROD || process.env.REACT_APP_GOOGLE_CLIENT_ID
      : process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return clientId || 'YOUR_GOOGLE_CLIENT_ID_HERE';
}

function withGoogleAuth(element) {
  return <GoogleOAuthProvider clientId={googleClientId()}>{element}</GoogleOAuthProvider>;
}

// Inner app component that can use useAuth (inside AuthProvider)
function AppInner() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const { loading, error, properties, prependProperty, refetch: refetchProperties } = useProperties();
  const { user, authedFetch, isAdmin } = useAuth();
  const { consent } = useCookieConsent();

  // Track page views on every route change
  useEffect(() => {
    logPageView(location.pathname + location.search);
  }, [location]);

  // `properties` is otherwise only fetched once at startup and shared
  // across every page via props (see useProperties.js) - refetch whenever
  // landing on a listings page so a change made elsewhere (a new listing,
  // an admin approval, a price edit) actually shows up without a hard
  // refresh. Runs in the background (no loading spinner) since the stale
  // list is still fine to show while the fresh one loads.
  useEffect(() => {
    const listingsPages = ['/browse', '/favorites', '/'];
    if (listingsPages.includes(location.pathname)) {
      refetchProperties({ background: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Reset scroll position on forward/replace navigation. Without this,
  // client-side navigation (e.g. Browse -> Property) keeps the old scroll
  // offset, so the new page renders mid-scroll and content like the
  // breadcrumb appears to "jump" once the user scrolls up to find it.
  // Skipped on POP (back/forward button) so the browser's own scroll
  // restoration for the page you're returning to still works.
  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);

  const filters = usePropertyFilters(properties);
  const propertyForm = usePropertyForm();
  const [appNotification, setAppNotification] = useState({ type: '', message: '' });
  const [propertySubmitting, setPropertySubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Closes the mobile menu sheet if it's left open across an in-app
  // navigation (e.g. the browser back button, not a tap inside the sheet
  // itself, which already closes it before navigating).
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!appNotification.message || appNotification.type === 'error') {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setAppNotification({ type: '', message: '' });
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [appNotification]);
  // const [showVerifyModal, setShowVerifyModal] = useState(false);
  // const [pendingPayload, setPendingPayload] = useState(null);

  // Navigate to property detail using SEO-friendly slug URL
  const openProperty = (id) => {
    const prop = properties.find((p) => String(p.id) === String(id));
    if (prop) {
      navigate(`/property/${generatePropertySlug(prop)}`);
    } else {
      navigate(`/property/${id}`);
    }
  };

  // Directly submit property without 2FA modal
  const isAdminListingMode = location.pathname.startsWith('/admin/list-property/');
  const adminTargetUserId = isAdminListingMode ? location.state?.targetUser?.id : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (propertySubmitting) {return;}
    setAppNotification({ type: '', message: '' });
    setPropertySubmitting(true);
    const payload = propertyForm.buildPayload();
    if (isAdminListingMode && adminTargetUserId) {
      payload.postedByUserId = adminTargetUserId;
    }
    try {
      const response = await authedFetch(`${API_BASE}/properties`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          setAppNotification({ type: 'warning', message: 'Please log in to list a property.' });
          navigate('/login');
        } else if (response.status === 403) {
          setAppNotification({
            type: 'warning',
            message: 'Only agents and admins can list properties.',
          });
          navigate('/');
        } else {
          console.error('Server error:', result);
          setAppNotification({ type: 'error', message: result.error || 'Failed to add property' });
        }
        return;
      }
      if (result.property) {
        prependProperty(result.property);
        trackEvent('listing_submitted', {
          property_id: result.property.id,
          listing_type: result.property.listing_type,
          state: result.property.state,
        });
      }
      setAppNotification({ type: 'success', message: 'Listing submitted successfully.' });
      navigate(isAdminListingMode ? '/admin/users' : '/browse');
      propertyForm.resetForm();
    } catch (err) {
      console.error('Submission error:', err);
      setAppNotification({
        type: 'error',
        message: 'Something went wrong. Please try again.',
      });
    } finally {
      setPropertySubmitting(false);
    }
  };

  const activePage =
    location.pathname === '/list-property'
      ? 'list-property'
      : location.pathname.startsWith('/agent')
        ? 'agent'
        : location.pathname.startsWith('/admin')
          ? 'admin'
          : location.pathname === '/favorites'
            ? 'favorites'
            : location.pathname === '/about'
              ? 'about'
              : location.pathname === '/contact'
                ? 'contact'
                : location.pathname === '/safety'
                  ? 'safety'
                  : location.pathname === '/become-an-agent'
                    ? 'become-agent'
                    : location.pathname.startsWith('/blog')
                      ? 'blog'
                      : location.pathname === '/login'
                        ? 'login'
                        : location.pathname === '/'
                          ? 'landing'
                          : 'browse';

  const isMessagesPage = location.pathname.startsWith('/messages');
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/confirm-email' ||
    location.pathname === '/check-your-email';
  const isSecurityPage = location.pathname === '/security';
  // Standalone, focused tasks (writing an article, a payment callback) skip
  // the bottom nav so nothing distracts from finishing them - same reasoning
  // as excluding the auth flow pages.
  const isStandaloneFlowPage =
    location.pathname.startsWith('/blog/write') || location.pathname === '/advertise/callback';
  const showMobileNav = !isAuthPage && !isStandaloneFlowPage;
  // The legal/info page family (About, Privacy, Cookie Policy, Terms, Safety,
  // Contact, Blog) all go edge-to-edge. Applying page-container--full here,
  // once, on the single <main> wrapper is what these pages' own root divs
  // used to do redundantly - each was also adding page-container/
  // page-container--full itself, doubling the padding on top of what <main>
  // already provides.
  const isLegalPage =
    [
      '/about',
      '/privacy',
      '/cookie-policy',
      '/terms',
      '/safety',
      '/contact',
      '/listing-policy',
      '/agent-verification-policy',
    ].includes(location.pathname) ||
    location.pathname.startsWith('/blog') ||
    location.pathname.startsWith('/help');
  return (
    <div className={`app-shell${showMobileNav ? ' app-shell--has-bottom-nav' : ''}`}>
      <SiteJsonLd />
      {consent?.functional && withGoogleAuth(<GoogleOneTapPrompt disabled={isAuthPage} />)}
      <TwoFactorPromptModal disabled={isAuthPage || isSecurityPage} />
      <SiteHeader
        activePage={activePage}
        onNavigate={navigate}
        filterType={filters.filterType}
        setFilterType={filters.setFilterType}
        setFilterPropertyType={filters.setFilterPropertyType}
      />

      <main
        className={`page-container${
          isMessagesPage
            ? ' page-container--messages page-container--full'
            : isLegalPage
              ? ' page-container--full'
              : ''
        }${showMobileNav ? ' page-container--has-bottom-nav' : ''}`}
      >
        {appNotification.message ? (
          <Alert type={appNotification.type || 'info'} srOnly style={{ marginBottom: '12px' }}>
            {appNotification.message}
          </Alert>
        ) : null}

        <Routes>
          <Route
            path="/"
            element={
              <LandingPage
                filterLocation={filters.filterLocation}
                filterArea={filters.filterArea}
                filterType={filters.filterType}
                browseAreas={filters.browseAreas}
                setFilterLocation={filters.setFilterLocation}
                setFilterArea={filters.setFilterArea}
                setFilterType={filters.setFilterType}
                featuredListings={filters.featuredListings}
                properties={properties}
                placeholderImage={placeholderImage}
                onBrowse={(type) =>
                  navigate(type ? `/browse?type=${encodeURIComponent(type)}` : '/browse')
                }
                onListProperty={() => navigate('/list-property')}
                onOpenProperty={openProperty}
              />
            }
          />

          <Route
            path="/browse"
            element={
              <BrowsePage
                filterLocation={filters.filterLocation}
                filterArea={filters.filterArea}
                filterType={filters.filterType}
                filterPropertyType={filters.filterPropertyType}
                sortBy={filters.sortBy}
                browseAreas={filters.browseAreas}
                setFilterLocation={filters.setFilterLocation}
                setFilterArea={filters.setFilterArea}
                setFilterType={filters.setFilterType}
                setFilterPropertyType={filters.setFilterPropertyType}
                setSortBy={filters.setSortBy}
                isFilterDrawerOpen={filters.isFilterDrawerOpen}
                setIsFilterDrawerOpen={filters.setIsFilterDrawerOpen}
                resetAllFilters={filters.resetAllFilters}
                filteredProperties={filters.filteredProperties}
                error={error}
                loading={loading}
                minPrice={filters.minPrice}
                maxPrice={filters.maxPrice}
                minBedrooms={filters.minBedrooms}
                minBathrooms={filters.minBathrooms}
                postedWithin={filters.postedWithin}
                setMinPrice={filters.setMinPrice}
                setMaxPrice={filters.setMaxPrice}
                setMinBedrooms={filters.setMinBedrooms}
                setMinBathrooms={filters.setMinBathrooms}
                setPostedWithin={filters.setPostedWithin}
                filterState={filters.filterFlags}
                filterSetters={filters.filterSetters}
                filterAmenities={filters.filterAmenities}
                toggleFilterAmenity={filters.toggleFilterAmenity}
                filterPropertyDocuments={filters.filterPropertyDocuments}
                toggleFilterPropertyDocument={filters.toggleFilterPropertyDocument}
                onOpenProperty={openProperty}
              />
            }
          />

          {/* List Property - Requires authentication */}
          <Route
            path="/list-property"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <ListPropertyPage
                    formState={propertyForm.formState}
                    formSetters={propertyForm.formSetters}
                    addFormAreas={propertyForm.addFormAreas}
                    onSubmit={handleSubmit}
                    submitting={propertySubmitting}
                  />
                </LazyRoute>
              </ProtectedRoute>
            }
          />

          {/* Edit an existing listing - full details, PATCH /properties/:id */}
          <Route
            path="/edit-property/:id"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <EditPropertyPage
                    formState={propertyForm.formState}
                    formSetters={propertyForm.formSetters}
                    addFormAreas={propertyForm.addFormAreas}
                    resetForm={propertyForm.resetForm}
                    buildPayload={propertyForm.buildPayload}
                  />
                </LazyRoute>
              </ProtectedRoute>
            }
          />

          {/* Admin: list a property on behalf of another user */}
          <Route
            path="/admin/list-property/:userId"
            element={
              user && isAdmin ? (
                <LazyRoute>
                  <ListPropertyPage
                    formState={propertyForm.formState}
                    formSetters={propertyForm.formSetters}
                    addFormAreas={propertyForm.addFormAreas}
                    onSubmit={handleSubmit}
                    submitting={propertySubmitting}
                    adminMode
                  />
                </LazyRoute>
              ) : user ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace state={{ from: '/admin' }} />
              )
            }
          />

          <Route
            path="/property/:slug"
            element={
              <PropertyDetailsPage properties={properties} loading={loading} error={error} />
            }
          />

          <Route path="/seller/:sellerId" element={<SellerProfilePage />} />

          {/* Unified settings page (Phase 1+) */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <UserSettingsPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />
          {/* Legacy /seller/settings → redirect to unified /settings */}
          <Route path="/seller/settings" element={<Navigate to="/settings" replace />} />

          {/* Public marketing page - anonymous visitors and logged-in
              buyers both see it; the application form only renders for a
              logged-in buyer, further down the same page. */}
          <Route
            path="/become-an-agent"
            element={
              <LazyRoute>
                <BecomeAgentPage />
              </LazyRoute>
            }
          />

          {/* Seller Profile Template Demo */}
          <Route
            path="/seller-profile-demo"
            element={
              <LazyRoute>
                <SellerProfileTemplateDemo />
              </LazyRoute>
            }
          />
          <Route path="/seller-guide" element={<SellerGuidePage />} />

          <Route
            path="/dashboard"
            element={
              user ? (
                <LazyRoute>
                  <DashboardPage />
                </LazyRoute>
              ) : (
                <Navigate to="/login" replace state={{ from: '/dashboard' }} />
              )
            }
          />

          <Route
            path="/agent"
            element={
              user ? (
                <LazyRoute>
                  <AgentDashboardPage />
                </LazyRoute>
              ) : (
                <Navigate to="/login" replace state={{ from: '/agent' }} />
              )
            }
          />

          <Route
            path="/admin/*"
            element={
              user && isAdmin ? (
                <LazyRoute>
                  <AdminDashboardPage />
                </LazyRoute>
              ) : user ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace state={{ from: '/admin' }} />
              )
            }
          />

          <Route path="/login" element={withGoogleAuth(<LoginPage onNavigate={navigate} />)} />

          <Route path="/signup" element={withGoogleAuth(<SignUpPage onNavigate={navigate} />)} />

          <Route
            path="/forgot-password"
            element={
              <LazyRoute>
                <ForgotPasswordPage onNavigate={navigate} />
              </LazyRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <LazyRoute>
                <ResetPasswordPage onNavigate={navigate} />
              </LazyRoute>
            }
          />

          <Route
            path="/confirm-email"
            element={
              <LazyRoute>
                <ConfirmEmailPage onNavigate={navigate} />
              </LazyRoute>
            }
          />

          <Route
            path="/check-your-email"
            element={
              <LazyRoute>
                <CheckYourEmailPage onNavigate={navigate} />
              </LazyRoute>
            }
          />

          <Route path="/advertise" element={<AdvertisePage onNavigate={navigate} />} />
          <Route
            path="/advertise/callback"
            element={
              <LazyRoute>
                <AdvertisePaymentCallbackPage onNavigate={navigate} />
              </LazyRoute>
            }
          />

          <Route
            path="/bookings"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <MyBookingsPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <MyReservationRequestsPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/security"
            element={
              user ? (
                <LazyRoute>
                  <TwoFactorSetupPage onNavigate={navigate} />
                </LazyRoute>
              ) : (
                <Navigate to="/login" replace state={{ from: '/security' }} />
              )
            }
          />

          <Route
            path="/favorites"
            element={
              <LazyRoute>
                <FavoritesPage properties={properties} onOpenProperty={openProperty} />
              </LazyRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <MessagesInboxPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />

          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/safety" element={<SafetyPage />} />
          <Route path="/listing-policy" element={<ListingPolicyPage />} />
          <Route
            path="/agent-verification-policy"
            element={
              ADMIN_FEATURE_FLAGS.agentRequests ? (
                <AgentVerificationPolicyPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/careers" element={<CareersPage />} />

          {/* Help Centre */}
          <Route path="/help" element={<HelpCentreIndexPage />} />
          <Route path="/help/:slug" element={<HelpArticlePage />} />

          {/* Blog / content pages */}
          <Route path="/blog" element={<BlogIndexPage />} />
          {/* Contributor applications are now part of the unified Become a
              Partner page - this old URL keeps working as a redirect so
              existing links/bookmarks don't break. */}
          <Route path="/blog/submit" element={<Navigate to="/become-an-agent?apply=write" replace />} />
          <Route path="/blog/guidelines" element={<BlogGuidelinesPage />} />
          {/* Contributor profiles are now folded into the unified /seller/:id
              profile (Listings + Articles tabs) - this old URL keeps
              working as a redirect so existing links/bookmarks don't break. */}
          <Route
            path="/blog/author/:userId"
            element={<RedirectToSellerProfile />}
          />
          <Route
            path="/blog/dashboard"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <ContributorDashboardPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/write"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <ArticleEditorPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/write/:id"
            element={
              <ProtectedRoute>
                <LazyRoute>
                  <ArticleEditorPage />
                </LazyRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />

          {/* Location landing pages: /lagos/apartments, /abuja/houses-for-rent, etc. */}
          <Route
            path="/:state/:filter?"
            element={
              <LocationLandingPage
                properties={properties}
                loading={loading}
                error={error}
                onOpenProperty={openProperty}
              />
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <SiteFooter />
      {showMobileNav && (
        <>
          <MobileBottomNav onOpenMenu={() => setMobileMenuOpen(true)} menuOpen={mobileMenuOpen} />
          <MobileMenuSheet isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
        </>
      )}
      <CookieConsentBanner />
      <CookieSettingsModal />
      <HelpBotWidget disabled={isAuthPage} />

      {/* 2FA verification modal removed for add listing flow */}
      {/* <VerifyContactModal ... /> */}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <CurrencyProvider>
            <CookieConsentProvider>
              <AppInner />
            </CookieConsentProvider>
          </CurrencyProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
